//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { saveBlob } from '@utils/saveBlob';

interface ErrorReportSection {
  heading?: string;
  /** List of error messages to display as bulleted items */
  items: string[];
}
interface BuildErrorReportTextProps {
  fileName?: string;
  reportTitle: string;
  sections: ErrorReportSection[];
  timeZone: TimeZoneId;
}

const FALLBACK_FILE_NAME = 'Unknown file';
const TEXT_PLAIN_CONTENT_TYPE = 'text/plain';
const BRACKETED_ERROR_BLOCK_REGEX = /\[[^\]]+\][\s\S]*?(?=\[[^\]]+\]|$)/g;
const LEADING_BRACKETED_ID_REGEX = /^\[([^\]]+)\]/;
const DELIMITED_ERROR_ITEM_REGEX = /\r?\n|;/;

/**
 * Builds formatted error report text for download.
 *
 * Generates a user-friendly plain text report with:
 * - Report title
 * - File name (if available)
 * - Current date in the user's timezone
 * - Sections with optional headings and bulleted error lists
 *
 * @param config - Configuration object for the error report
 * @returns Formatted plain text string ready for download
 *
 */
export const buildErrorReportText = ({
  fileName,
  reportTitle,
  sections,
  timeZone,
}: BuildErrorReportTextProps): string => {
  const dateString = DateString.fromTimestamp(Date.now(), timeZone);
  const currentDate = DateString.format(dateString, 'MMM D, YYYY');

  const headerLines = [
    reportTitle,
    `File: ${fileName ?? FALLBACK_FILE_NAME}`,
    `Date: ${currentDate}`,
    '',
  ];

  const header = headerLines.join('\n');

  const sectionContent = sections
    .map(({ heading, items }) => {
      const formattedItems = items.map((item) => `• ${item}`).join('\n');

      return heading ? `${heading}\n${formattedItems}` : formattedItems;
    })
    .join('\n\n');

  return `${header}\n${sectionContent}`;
};

export const splitBracketedErrorBlocks = (errorMessage: string): string[] =>
  errorMessage.match(BRACKETED_ERROR_BLOCK_REGEX) ?? [];

export const extractBracketedId = (errorBlock: string): string | undefined =>
  LEADING_BRACKETED_ID_REGEX.exec(errorBlock)?.[1];

export const stripLeadingBracketedId = (errorBlock: string): string =>
  errorBlock.replace(LEADING_BRACKETED_ID_REGEX, '').trim();

export const splitDelimitedErrorItems = (errorText: string): string[] =>
  errorText
    .split(DELIMITED_ERROR_ITEM_REGEX)
    .map((error) => error.trim())
    .filter(Boolean);

export const downloadErrorReportText = (
  content: string,
  downloadFileName: string,
): void => {
  saveBlob(content, downloadFileName, {
    'content-type': TEXT_PLAIN_CONTENT_TYPE,
  });
};
